import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import RangeFields from 'components/ReportRequestV2/RangeFields';

import styles from './FlavorConstraintPicker.module.css';

export type FlavorConstraintMode = 'free' | 'lock' | 'remove' | 'tune';
type ActiveMode = Exclude<FlavorConstraintMode, 'free'>;

export interface FlavorOption {
  flavor: string;
  label: string;
  mode: FlavorConstraintMode;
  /** The target product's share of the flavor, in percent. */
  sharePercent?: number;
  inReport: boolean;
}

export interface FlavorConstraintPickerProps {
  /** Every flavor that can be constrained, constrained ones included. */
  options: FlavorOption[];
  /** The product's top unconstrained flavors, offered as one-click adds. */
  suggestions: FlavorOption[];
  tuneRanges: ReadonlyMap<string, { min: number; max: number }>;
  onModeChange: (flavors: string[], mode: FlavorConstraintMode) => void;
  onTuneRangeChange: (flavor: string, range: { min: number; max: number }) => void;
  tuneBounds: [number, number];
  loading?: boolean;
  /** Explains why there are no product shares or suggestions, if so. */
  notice?: string;
  /** Whether the target product's shares are known, so a missing share means 0%. */
  sharesKnown?: boolean;
  /** Search every flavor rather than only the product's decomp flavors. */
  offerAll: boolean;
  /** Absent when all flavors are always offered, e.g. without product shares. */
  onOfferAllChange?: (offerAll: boolean) => void;
  tuneErrors?: Record<string, string>;
  error?: string;
}

const MODES: { mode: ActiveMode; label: string }[] = [
  { mode: 'lock', label: 'Lock' },
  { mode: 'remove', label: 'Remove' },
  { mode: 'tune', label: 'Tune' },
];

/** New constraints start as Tune (2–8%); the row's control changes it. */
const ADD_MODE: ActiveMode = 'tune';

const currentText = (o: FlavorOption, sharesKnown: boolean) => {
  if (o.sharePercent !== undefined) return `${o.sharePercent}%`;
  return sharesKnown ? '0%' : 'unknown';
};

const rowHint = (o: FlavorOption, sharesKnown: boolean) => {
  const now = currentText(o, sharesKnown);
  if (o.mode === 'lock') return now === 'unknown' ? 'Kept at its current share' : `Kept at ${now}`;
  return 'Removed from the recipe';
};

const NOT_IN_REPORT = 'Not in the parent report, so the optimizer may not be able to add it.';
const OPTION_LIMIT = 100;
const filterOptions = createFilterOptions<FlavorOption>({ limit: OPTION_LIMIT, stringify: (o) => `${o.label} ${o.flavor}` });

const groupOf = (o: FlavorOption) => (o.sharePercent !== undefined ? "In this product's decomp" : 'All flavors');

const share = (o: FlavorOption) =>
  o.sharePercent !== undefined && <span className={styles.share}>{o.sharePercent}%</span>;

/**
 * Add flavors by searching (or one-click from the product's top flavors), then
 * set each constrained flavor's mode on its own row. Scales to hundreds of flavors.
 */
const FlavorConstraintPicker: React.FC<FlavorConstraintPickerProps> = ({
  options,
  suggestions,
  tuneRanges,
  onModeChange,
  onTuneRangeChange,
  tuneBounds,
  loading,
  notice,
  sharesKnown = false,
  offerAll,
  onOfferAllChange,
  tuneErrors = {},
  error,
}) => {
  const [input, setInput] = React.useState('');
  const constrained = options.filter((o) => o.mode !== 'free');
  const free = options.filter((o) => o.mode === 'free');
  const add = (o: FlavorOption) => onModeChange([o.flavor], ADD_MODE);

  return (
    <Box className={styles.picker}>
      <Box className={styles.addRow}>
        <Autocomplete<FlavorOption, false, false, false>
          className={styles.search}
          options={free}
          value={null}
          inputValue={input}
          onInputChange={(_, value, reason) => reason !== 'reset' && setInput(value)}
          onChange={(_, o) => {
            if (o) add(o);
            setInput('');
          }}
          filterOptions={filterOptions}
          groupBy={groupOf}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.flavor === b.flavor}
          loading={loading}
          loadingText="Loading flavors…"
          blurOnSelect={false}
          clearOnBlur={false}
          noOptionsText={
            input ? `No matching ${offerAll ? '' : 'decomp '}flavors` : 'No more flavors to add'
          }
          renderOption={(props, o) => (
            <li {...props} key={o.flavor}>
              <Box className={styles.option}>
                <span>{o.label}</span>
                {share(o)}
                {!o.inReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not in parent report
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Add a flavor constraint"
              placeholder={`${free.length.toLocaleString()} ${offerAll ? 'flavors' : 'decomp flavors'}`}
            />
          )}
        />
        {onOfferAllChange && (
          // Fixed-size segments, so switching never resizes the search or shifts the layout.
          <ToggleButtonGroup
            size="small"
            exclusive
            value={offerAll ? 'all' : 'decomp'}
            onChange={(_, next: 'all' | 'decomp' | null) => next && onOfferAllChange(next === 'all')}
            aria-label="Flavors to search"
            className={styles.scope}
          >
            <ToggleButton value="decomp">Decomp</ToggleButton>
            <ToggleButton value="all">All flavors</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {suggestions.length > 0 && (
        <Box className={styles.suggestions} role="group" aria-label="Top flavors in this product">
          <Typography variant="caption" color="text.secondary" className={styles.caption}>
            Top flavors in this product, click to add:
          </Typography>
          {suggestions.map((o) => (
            <Chip
              key={o.flavor}
              size="small"
              variant="outlined"
              icon={<AddIcon />}
              label={`${o.label} ${o.sharePercent}%`}
              onClick={() => add(o)}
              aria-label={`Add ${o.label}, ${o.sharePercent}% of the product`}
            />
          ))}
        </Box>
      )}
      {notice && (
        <Typography variant="body2" color="text.secondary">
          {notice}
        </Typography>
      )}

      {constrained.length === 0 ? (
        <Typography variant="body2" color="text.secondary" className={styles.empty}>
          No constraints: the optimizer may change every flavor.
        </Typography>
      ) : (
        <Box className={styles.rows} role="list" aria-label="Constrained flavors">
          {constrained.map((o) => {
            const range = tuneRanges.get(o.flavor) ?? { min: tuneBounds[0], max: tuneBounds[1] };
            return (
              <Box key={o.flavor} role="listitem" className={styles.row} aria-label={o.label}>
                <Box className={styles.name}>
                  <Typography variant="body2" fontWeight={600}>
                    {o.label}
                  </Typography>
                  {!o.inReport && (
                    <Tooltip title={NOT_IN_REPORT}>
                      <WarningAmberIcon fontSize="small" color="warning" titleAccess={NOT_IN_REPORT} />
                    </Tooltip>
                  )}
                </Box>
                <Box className={styles.current} aria-label={`Current decomp ${currentText(o, sharesKnown)}`}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Current
                  </Typography>
                  <Typography variant="body1" fontWeight={600} component="div">
                    {currentText(o, sharesKnown) === 'unknown' ? '—' : currentText(o, sharesKnown)}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={o.mode}
                  onChange={(_, next: ActiveMode | null) => next && onModeChange([o.flavor], next)}
                  aria-label={`${o.label} constraint`}
                >
                  {MODES.map((m) => (
                    <ToggleButton key={m.mode} value={m.mode}>
                      {m.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {o.mode === 'tune' ? (
                  <Box className={styles.range}>
                    <RangeFields
                      label={o.label}
                      min={range.min}
                      max={range.max}
                      onChange={(next) => onTuneRangeChange(o.flavor, next)}
                      bounds={tuneBounds}
                      unit="%"
                      error={!!tuneErrors[o.flavor]}
                      sliderClassName={styles.slider}
                      numberClassName={styles.number}
                      marks={
                        o.sharePercent !== undefined && o.sharePercent <= tuneBounds[1]
                          ? [{ value: o.sharePercent, label: `now ${o.sharePercent}%` }]
                          : undefined
                      }
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" className={styles.range}>
                    {rowHint(o, sharesKnown)}
                  </Typography>
                )}
                <IconButton size="small" aria-label={`Clear ${o.label}`} onClick={() => onModeChange([o.flavor], 'free')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                {tuneErrors[o.flavor] && (
                  <FormHelperText error className={styles.rowError}>
                    {tuneErrors[o.flavor]}
                  </FormHelperText>
                )}
              </Box>
            );
          })}
          <Box>
            <Button size="small" onClick={() => onModeChange(constrained.map((o) => o.flavor), 'free')}>
              Clear all
            </Button>
          </Box>
        </Box>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default FlavorConstraintPicker;
